import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { Card, Button, InlineSpinner } from '@/components/ui'
import { forumApi } from '@/api'
import type { ForumCategory } from '@/api/forum'
import toast from 'react-hot-toast'

export default function NewThreadPage() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<ForumCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    forum: 0,
    title: '',
    content: '',
  })

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      const data = await forumApi.getCategories()
      const categoriesArray = Array.isArray(data) ? data : (data as any).results || []
      setCategories(categoriesArray)
      if (categoriesArray.length > 0) {
        setFormData(prev => ({ ...prev, forum: categoriesArray[0].id }))
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      toast.error('Errore nel caricamento delle categorie')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.forum) {
      toast.error('Seleziona una categoria')
      return
    }

    if (!formData.title.trim()) {
      toast.error('Inserisci un titolo')
      return
    }

    if (!formData.content.trim()) {
      toast.error('Inserisci il contenuto')
      return
    }

    setSubmitting(true)
    try {
      const thread = await forumApi.createThread(formData)
      toast.success('Discussione creata con successo!')
      navigate(`/forum/thread/${thread.id}`)
    } catch (error: any) {
      console.error('Error creating thread:', error)
      toast.error(error.response?.data?.detail || 'Errore nella creazione della discussione')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento..." />
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nuova discussione</h1>
          <p className="text-gray-600">Crea una nuova discussione nel forum</p>
        </div>
      </div>

      {categories.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna categoria disponibile</h3>
            <p className="text-gray-500">Non ci sono categorie nel forum. Contatta un amministratore.</p>
          </div>
        </Card>
      ) : (
        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                id="category"
                value={formData.forum}
                onChange={(e) => setFormData({ ...formData, forum: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Inserisci un titolo descrittivo..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Contenuto *
              </label>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                placeholder="Scrivi il contenuto della tua discussione..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Puoi usare markdown per formattare il testo.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Pubblicazione...' : 'Pubblica discussione'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
